import React, { useMemo, useCallback } from 'react';
import { useCountryDelete, useCountryGetFilters, useCountryList } from '../hooks/use-country.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { Country } from '../types/country.types';
import CountryTable from '../components/country-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { WithSysAdminPermissionCheck } from '@components/auth/WithSysAdminPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';

const CountryTableContainer = () => {
  const { t } = useTranslation();
  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } = useCountryGetFilters();

  const { data, isLoading, isFetching } = useCountryList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteCountryMutation = useCountryDelete();

  const handleDeleteCountry = useCallback(async (record: Country) => {
    try {
      await deleteCountryMutation.mutateAsync(record.id);
      message.success(t('crud.delete.success', { entity: t('countries.title') }));
    } catch (error) {
      const errorMessage = extractCreateError(
        error,
        t('crud.delete.error', { entity: t('countries.title') })
      );
      message.error(errorMessage);
    }
  }, []);

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<Country>[]>(
    () => [
      {
        title: t('countries.name.label'),
        key: 'name',
        align: 'left',
        render: (_: any, record: Country) => record.name,
      },
      {
        title: t('countries.code.label'),
        key: 'code',
        align: 'left',
        render: (_: any, record: Country) => record.code,
      },
      {
        title: t('countries.timezone.label'),
        key: 'timezone',
        align: 'left',
        render: (_: any, record: Country) => record.timezone,
      },
      {
        title: t('common.actions'),
        key: 'actions',
        align: 'right',
        render: (_: any, record: Country) => (
          <ActionButtons
            record={record}
            baseUrl="/dashboard/admin/country"
            onDelete={handleDeleteCountry}
            isDeleting={
              deleteCountryMutation.isPending && deleteCountryMutation.variables === record.id
            }
          />
        ),
      },
    ],
    [handleDeleteCountry, deleteCountryMutation.isPending, deleteCountryMutation.variables]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('countries.title') })}
        createRoute="/dashboard/admin/country/create"
        showCreateButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <CountryTable
          data={tableData}
          columns={columns}
          loading={
            isLoading || deleteCountryMutation.isPending || isFetching || isAvailableFiltersLoading
          }
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={availableFilters?.data}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(CountryTableContainer, 'country.view');
